import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../../firebase";
import {
  collection, query, where, onSnapshot, doc, updateDoc, setDoc, getDoc, serverTimestamp,
} from "firebase/firestore";
import { normalizePhoneUY, toWaMeDigits } from "../../utils/phoneE164";
import { useBuilding } from "../../context/BuildingContext";
import { fetchUsersByField, tolerantId } from "../../utils/firestoreHelpers";
import {
  colors, radius, spacing, typography, cardBase, inputBase, btnPrimary, btnSecondary,
  pageTitle, pageSubtitle,
} from "../../utils/designTokens";
import AdminModal from "../../components/AdminModal";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import FormField from "../../components/FormField";
import {
  FaRegComments, FaPhoneAlt, FaReply, FaCheck, FaRegClock, FaWhatsapp,
  FaRegCalendarAlt, FaChevronLeft, FaChevronRight,
} from "react-icons/fa";

// Bandeja de consultas que los residentes dejan via Habi (colección habi_consultas):
// mensajes para la administración y llamadas agendadas. El admin responde desde acá
// (misma escritura que la tool responder_consulta de Habi) y configura su
// disponibilidad horaria para las llamadas (settings/admin_availability_{bid}).

const FILTROS = [
  { key: "pendiente", label: "Pendientes" },
  { key: "respondida", label: "Respondidas" },
  { key: "resuelta", label: "Resueltas" },
  { key: "todas", label: "Todas" },
];

const DIAS = [
  { v: 1, label: "Lun" }, { v: 2, label: "Mar" }, { v: 3, label: "Mié" },
  { v: 4, label: "Jue" }, { v: 5, label: "Vie" }, { v: 6, label: "Sáb" }, { v: 0, label: "Dom" },
];

const DISPO_DEFAULT = {
  availableDays: [1, 2, 3, 4, 5],
  startHour: 9,
  endHour: 18,
  slotMinutes: 30,
  minAdvanceHours: 2,
  maxAdvanceDays: 14,
};

/** userid canónico para comparar: 11, "11" y "011" son el mismo. */
const claveUserid = (v) => {
  const s = String(v ?? "").trim();
  return /^\d+$/.test(s) ? String(Number(s)) : s;
};

const fmtFecha = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("es-UY", { day: "numeric", month: "short" }) +
      " " + d.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

// Cómo le llegó al residente la respuesta (lo escribe el trigger
// onConsultaAnswered en functions).
const ENTREGA = {
  whatsapp: { label: "✓ Le llegó por WhatsApp", color: colors.successDark },
  whatsapp_plantilla: { label: "✓ Le llegó por WhatsApp (mensaje automático)", color: colors.successDark },
  email: { label: "✓ Le llegó por email (WhatsApp no disponible)", color: colors.successDark },
  app: { label: "Solo visible en la app: no se pudo enviar por WhatsApp ni email", color: colors.warningDark },
  error: { label: "No se pudo enviar al residente", color: colors.errorDark },
};

export default function ConsultasAdmin() {
  const { selectedBuilding } = useBuilding();
  const buildingid = selectedBuilding?.buildingid;

  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("pendiente");
  const [responder, setResponder] = useState(null); // consulta en el modal
  const [respuesta, setRespuesta] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [dispoOpen, setDispoOpen] = useState(false);
  const [dispo, setDispo] = useState(DISPO_DEFAULT);
  const [dispoGuardando, setDispoGuardando] = useState(false);
  const [semanaOffset, setSemanaOffset] = useState(0);

  // Semana visible (lunes a domingo) para el calendario de la agenda.
  const diasSemana = useMemo(() => {
    const hoy = new Date();
    const lunes = new Date(hoy);
    lunes.setHours(0, 0, 0, 0);
    lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7) + semanaOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(lunes);
      d.setDate(lunes.getDate() + i);
      return d;
    });
  }, [semanaOffset]);

  const isoDia = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Llamadas agendadas indexadas por celda fecha_hora del calendario.
  const llamadasPorCelda = useMemo(() => {
    const m = {};
    for (const c of consultas) {
      if (c.type !== "llamada" || !c.call?.date || !c.call?.time) continue;
      const h = parseInt(String(c.call.time).split(":")[0], 10);
      if (Number.isNaN(h)) continue;
      const key = `${c.call.date}_${h}`;
      (m[key] = m[key] || []).push(c);
    }
    return m;
  }, [consultas]);

  useEffect(() => {
    if (!buildingid) return undefined;
    const myEmail = (auth.currentUser?.email || "").toLowerCase();
    if (!myEmail) return undefined;
    setLoading(true);
    // adminEmails (array-contains con MI email) + edificio ('in' número/string):
    // las reglas 0D piden las dos cosas, la bandeja y ser staff VIGENTE de ese
    // edificio (un ex-admin que quedó en adminEmails ya no lee).
    const q = query(
      collection(db, "habi_consultas"),
      where("adminEmails", "array-contains", myEmail),
      where("buildingid", "in", tolerantId(buildingid))
    );
    const variants = tolerantId(buildingid).map(String);
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => variants.includes(String(c.buildingid)));
      rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setConsultas(rows);
      setLoading(false);
    }, (err) => {
      console.error("ConsultasAdmin listener:", err);
      setLoading(false);
    });
    return unsub;
  }, [buildingid]);

  const visibles = useMemo(() => {
    if (filtro === "todas") return consultas;
    return consultas.filter((c) => (c.status || "pendiente") === filtro);
  }, [consultas, filtro]);

  // Teléfonos de los residentes de las consultas (users.celular), para que el
  // admin pueda llamarlos con un clic — el doc de la consulta no trae el celular.
  // Reglas 0D (C10): el staff lee fichas ajenas solo con accessBids del
  // edificio, así que van todas juntas por fetchUsersByField (userid in, con
  // sus variantes number/string). Como el servidor: la ficha vigente primero y
  // el primer celular cargado.
  const [telefonos, setTelefonos] = useState({});
  useEffect(() => {
    // Misma clave que usa la tarjeta: String(residentUserid).
    const ids = [...new Set(consultas.map((c) => String(c.residentUserid ?? "")).filter((id) => id.trim() !== ""))]
      .filter((id) => telefonos[id] === undefined);
    if (ids.length === 0 || !buildingid) return;
    (async () => {
      const nuevos = Object.fromEntries(ids.map((id) => [id, null]));
      try {
        const variantes = [...new Map(ids.flatMap((id) => tolerantId(id)).map((v) => [`${typeof v}:${v}`, v])).values()];
        const fichas = await fetchUsersByField("userid", variantes, buildingid);
        const vigente = (u) => !u.archivedAt && u.mergedIntoUserid == null;
        const ordenadas = [...fichas].sort((a, b) => Number(!vigente(a)) - Number(!vigente(b)));
        for (const id of ids) {
          const cel = ordenadas
            .filter((u) => claveUserid(u.userid) === claveUserid(id))
            .map((u) => String(u.celular || "").trim())
            .find(Boolean);
          nuevos[id] = cel ? (normalizePhoneUY(cel) || cel) : null;
        }
      } catch (e) {
        console.warn("ConsultasAdmin: no se pudieron leer los teléfonos", e?.code || e?.message || e);
      }
      setTelefonos((t) => ({ ...t, ...nuevos }));
    })();
  }, [consultas]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendientes = useMemo(
    () => consultas.filter((c) => (c.status || "pendiente") === "pendiente").length,
    [consultas]
  );

  const abrirResponder = (c) => {
    setResponder(c);
    setRespuesta(c.adminResponse || "");
  };

  // Misma escritura que la tool responder_consulta de Habi (respond_consulta).
  const guardarRespuesta = async (resolver) => {
    if (!responder || guardando) return;
    if (!respuesta.trim() && !resolver) return;
    setGuardando(true);
    try {
      await updateDoc(doc(db, "habi_consultas", responder.id), {
        adminResponse: respuesta.trim(),
        respondedBy: auth.currentUser?.email || "admin",
        respondedAt: serverTimestamp(),
        status: resolver ? "resuelta" : "respondida",
        updatedAt: serverTimestamp(),
      });
      setResponder(null);
      setRespuesta("");
    } catch (e) {
      console.error("Error respondiendo consulta:", e);
    } finally {
      setGuardando(false);
    }
  };

  const marcarResuelta = async (c) => {
    try {
      await updateDoc(doc(db, "habi_consultas", c.id), {
        status: "resuelta",
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("Error marcando resuelta:", e);
    }
  };

  const abrirDispo = async () => {
    setDispoOpen(true);
    try {
      const snap = await getDoc(doc(db, "settings", `admin_availability_${Number(buildingid)}`));
      setDispo(snap.exists() ? { ...DISPO_DEFAULT, ...snap.data() } : DISPO_DEFAULT);
    } catch {
      setDispo(DISPO_DEFAULT);
    }
  };

  const guardarDispo = async () => {
    if (dispoGuardando) return;
    setDispoGuardando(true);
    try {
      await setDoc(
        doc(db, "settings", `admin_availability_${Number(buildingid)}`),
        { ...dispo, timezone: "America/Montevideo", updatedAt: serverTimestamp() },
        { merge: true }
      );
      setDispoOpen(false);
    } catch (e) {
      console.error("Error guardando disponibilidad:", e);
    } finally {
      setDispoGuardando(false);
    }
  };

  const toggleDia = (v) => setDispo((d) => {
    const tiene = (d.availableDays || []).includes(v);
    return {
      ...d,
      availableDays: tiene
        ? d.availableDays.filter((x) => x !== v)
        : [...(d.availableDays || []), v].sort(),
    };
  });

  if (!buildingid) {
    return (
      <div style={{ padding: spacing[6] }}>
        <EmptyState icon={FaRegComments} title="Elegí un edificio" description="Seleccioná un edificio para ver sus consultas." />
      </div>
    );
  }

  return (
    <div style={{ padding: spacing[6], maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: spacing[4], marginBottom: spacing[5] }}>
        <div>
          <h1 style={pageTitle}>Consultas</h1>
          <p style={{ ...pageSubtitle, marginTop: 4 }}>
            Mensajes y llamadas que los residentes dejan con Habi.
            {pendientes > 0 ? ` ${pendientes} pendiente${pendientes === 1 ? "" : "s"}.` : " Sin pendientes."}
          </p>
        </div>
        <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={abrirDispo}>
          <FaRegCalendarAlt /> Agenda y disponibilidad
        </button>
      </div>

      <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[4], flexWrap: "wrap" }}>
        {FILTROS.map((f) => {
          const activo = filtro === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFiltro(f.key)}
              style={{
                padding: "6px 14px",
                borderRadius: radius.full,
                border: `1px solid ${activo ? colors.primary : colors.gray200}`,
                background: activo ? colors.primarySubtle : colors.white,
                color: activo ? colors.primary : colors.gray600,
                fontSize: typography.sizes.base,
                fontWeight: 400,
                cursor: "pointer",
                transition: "all 0.15s ease",
                fontFamily: "inherit",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: spacing[8], color: colors.gray600 }}>Cargando…</div>
      ) : visibles.length === 0 ? (
        <EmptyState
          icon={FaRegComments}
          title={filtro === "pendiente" ? "Sin consultas pendientes" : "Nada por acá"}
          description="Cuando un residente le deje una consulta o agende una llamada con Habi, aparece en esta bandeja."
        />
      ) : (
        <div style={{ display: "grid", gap: spacing[3] }}>
          {visibles.map((c) => {
            const esLlamada = c.type === "llamada";
            return (
              <div key={c.id} style={{ ...cardBase, padding: spacing[5] }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: spacing[3] }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: radius.sm, flexShrink: 0,
                    background: esLlamada ? colors.warningLight : colors.primaryLighter,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {esLlamada
                      ? <FaPhoneAlt size={15} color={colors.warningDark} />
                      : <FaRegComments size={16} color={colors.primary} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" }}>
                      <span style={{ fontSize: typography.sizes.md, color: colors.black }}>
                        {c.residentName || "Residente"}
                      </span>
                      {c.unit && (
                        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>· Unidad {c.unit}</span>
                      )}
                      {telefonos[String(c.residentUserid)] && (
                        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "inline-flex", alignItems: "center", gap: 6 }}>
                          ·
                          <a href={`tel:${telefonos[String(c.residentUserid)]}`} style={{ color: colors.primary, textDecoration: "none" }} title="Llamar">
                            <FaPhoneAlt size={10} style={{ marginRight: 3 }} />
                            {telefonos[String(c.residentUserid)]}
                          </a>
                          <a
                            href={`https://wa.me/${toWaMeDigits(telefonos[String(c.residentUserid)])}`}
                            target="_blank" rel="noreferrer" title="Escribirle por WhatsApp"
                            style={{ color: colors.success, display: "inline-flex" }}
                          >
                            <FaWhatsapp size={13} />
                          </a>
                        </span>
                      )}
                      {c.channel === "whatsapp" && !telefonos[String(c.residentUserid)] && <FaWhatsapp size={13} color={colors.success} />}
                      <StatusBadge status={c.status || "pendiente"} size="sm" />
                      <span style={{ marginLeft: "auto", fontSize: typography.sizes.sm, color: colors.gray400, whiteSpace: "nowrap" }}>
                        <FaRegClock size={10} style={{ marginRight: 4 }} />
                        {fmtFecha(c.createdAt)}
                      </span>
                    </div>
                    {esLlamada && c.call && (
                      <div style={{
                        marginTop: spacing[2], fontSize: typography.sizes.base, color: colors.warningDark,
                        background: colors.warningLight, borderRadius: radius.sm,
                        padding: `${spacing[1]}px ${spacing[3]}px`, display: "inline-block",
                      }}>
                        📞 Llamada agendada: {c.call.label || `${c.call.date} ${c.call.time}`}
                      </div>
                    )}
                    <p style={{ margin: `${spacing[2]}px 0 0`, fontSize: typography.sizes.md, color: colors.gray900, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                      {c.message}
                    </p>
                    {c.adminResponse && (
                      <div style={{
                        marginTop: spacing[3], padding: spacing[3], background: colors.gray50,
                        borderRadius: radius.sm, fontSize: typography.sizes.base, color: colors.gray700,
                      }}>
                        <span style={{ color: colors.gray400, fontSize: typography.sizes.sm }}>
                          Respuesta ({c.respondedBy || "admin"} · {fmtFecha(c.respondedAt)}):
                        </span>
                        <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{c.adminResponse}</div>
                        {c.channel === "whatsapp" && (
                          <div style={{ marginTop: 6, fontSize: typography.sizes.sm, color: ENTREGA[c.deliveredVia]?.color || colors.gray400 }}>
                            {ENTREGA[c.deliveredVia]?.label || "Enviando al residente por WhatsApp…"}
                            {c.deliveryNote ? ` (${c.deliveryNote})` : ""}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: spacing[2], marginTop: spacing[3] }}>
                      {(c.status || "pendiente") !== "resuelta" && (
                        <>
                          <button type="button" style={{ ...btnPrimary, padding: "8px 16px", fontSize: 13 }} className="habitia-btn-primary" onClick={() => abrirResponder(c)}>
                            <FaReply size={11} /> Responder
                          </button>
                          <button type="button" style={{ ...btnSecondary, padding: "8px 16px", fontSize: 13 }} className="habitia-btn-secondary" onClick={() => marcarResuelta(c)}>
                            <FaCheck size={11} /> Marcar resuelta
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal responder */}
      <AdminModal
        open={!!responder}
        onClose={() => setResponder(null)}
        title={`Responder a ${responder?.residentName || "residente"}`}
        size="medium"
        icon={<FaReply />}
        footer={
          <>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={() => setResponder(null)}>
              Cancelar
            </button>
            <button
              type="button"
              style={{ ...btnSecondary, opacity: guardando ? 0.6 : 1 }}
              className="habitia-btn-secondary"
              disabled={guardando}
              onClick={() => guardarRespuesta(true)}
            >
              <FaCheck size={11} /> Responder y resolver
            </button>
            <button
              type="button"
              style={{ ...btnPrimary, opacity: guardando || !respuesta.trim() ? 0.6 : 1 }}
              className="habitia-btn-primary"
              disabled={guardando || !respuesta.trim()}
              onClick={() => guardarRespuesta(false)}
            >
              {guardando ? "Guardando…" : "Responder"}
            </button>
          </>
        }
      >
        {responder && (
          <div>
            <p style={{ margin: `0 0 ${spacing[3]}px`, fontSize: typography.sizes.base, color: colors.gray700, background: colors.gray50, padding: spacing[3], borderRadius: radius.sm, whiteSpace: "pre-wrap" }}>
              {responder.message}
            </p>
            <FormField label="Tu respuesta" htmlFor="consulta-respuesta">
              <textarea
                id="consulta-respuesta"
                className="habitia-input"
                style={{ ...inputBase, width: "100%", minHeight: 110, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }}
                placeholder="Escribí la respuesta para el residente…"
                value={respuesta}
                onChange={(e) => setRespuesta(e.target.value)}
              />
            </FormField>
            <p style={{ margin: `${spacing[2]}px 0 0`, fontSize: typography.sizes.sm, color: colors.gray400 }}>
              El residente la ve en "Mis consultas" y preguntándole a Habi.
            </p>
          </div>
        )}
      </AdminModal>

      {/* Agenda semanal: disponibilidad (días/horario) + llamadas agendadas */}
      <AdminModal
        open={dispoOpen}
        onClose={() => setDispoOpen(false)}
        title="Agenda y disponibilidad"
        size="wide"
        icon={<FaRegCalendarAlt />}
        footer={
          <>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={() => setDispoOpen(false)}>
              Cancelar
            </button>
            <button type="button" style={{ ...btnPrimary, opacity: dispoGuardando ? 0.6 : 1 }} className="habitia-btn-primary" disabled={dispoGuardando} onClick={guardarDispo}>
              {dispoGuardando ? "Guardando…" : "Guardar disponibilidad"}
            </button>
          </>
        }
      >
        <p style={{ margin: `0 0 ${spacing[3]}px`, fontSize: typography.sizes.base, color: colors.gray600 }}>
          Las celdas azules son tu disponibilidad (lo que Habi ofrece para agendar llamadas) y las tarjetas son las llamadas ya agendadas.
          Hacé clic en el encabezado de un día para activarlo o desactivarlo.
        </p>

        {/* Config de horario (arriba de la grilla) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: spacing[3], marginBottom: spacing[4] }}>
          <FormField label="Desde (hora)" htmlFor="dispo-desde">
            <input id="dispo-desde" type="number" min="0" max="23" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }}
              value={dispo.startHour} onChange={(e) => setDispo((d) => ({ ...d, startHour: Number(e.target.value) }))} />
          </FormField>
          <FormField label="Hasta (hora)" htmlFor="dispo-hasta">
            <input id="dispo-hasta" type="number" min="1" max="24" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }}
              value={dispo.endHour} onChange={(e) => setDispo((d) => ({ ...d, endHour: Number(e.target.value) }))} />
          </FormField>
          <FormField label="Turno (min)" htmlFor="dispo-slot">
            <select id="dispo-slot" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }}
              value={dispo.slotMinutes} onChange={(e) => setDispo((d) => ({ ...d, slotMinutes: Number(e.target.value) }))}>
              <option value={15}>15</option>
              <option value={30}>30</option>
              <option value={45}>45</option>
              <option value={60}>60</option>
            </select>
          </FormField>
          <FormField label="Anticipación (hs)" htmlFor="dispo-min">
            <input id="dispo-min" type="number" min="0" max="72" className="habitia-input" style={{ ...inputBase, width: "100%", boxSizing: "border-box" }}
              value={dispo.minAdvanceHours} onChange={(e) => setDispo((d) => ({ ...d, minAdvanceHours: Number(e.target.value) }))} />
          </FormField>
        </div>

        {/* Navegación de semana */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: spacing[2] }}>
          <button type="button" style={{ ...btnSecondary, padding: "6px 12px", fontSize: 13 }} className="habitia-btn-secondary" onClick={() => setSemanaOffset((o) => o - 1)}>
            <FaChevronLeft size={10} /> Semana anterior
          </button>
          <span style={{ fontSize: typography.sizes.md, color: colors.black }}>
            {diasSemana[0].toLocaleDateString("es-UY", { day: "numeric", month: "short" })} — {diasSemana[6].toLocaleDateString("es-UY", { day: "numeric", month: "short" })}
            {semanaOffset === 0 ? " (esta semana)" : ""}
          </span>
          <button type="button" style={{ ...btnSecondary, padding: "6px 12px", fontSize: 13 }} className="habitia-btn-secondary" onClick={() => setSemanaOffset((o) => o + 1)}>
            Semana siguiente <FaChevronRight size={10} />
          </button>
        </div>

        {/* Grilla semanal */}
        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "52px repeat(7, 1fr)", gap: 2, minWidth: 640 }}>
            <div />
            {diasSemana.map((d) => {
              const activo = (dispo.availableDays || []).includes(d.getDay());
              const esHoy = isoDia(d) === isoDia(new Date());
              const label = DIAS.find((x) => x.v === d.getDay())?.label || "";
              return (
                <button
                  key={isoDia(d)}
                  type="button"
                  onClick={() => toggleDia(d.getDay())}
                  title={activo ? "Clic para desactivar este día" : "Clic para activar este día"}
                  style={{
                    padding: "8px 4px", borderRadius: radius.sm, fontFamily: "inherit",
                    border: `1px solid ${activo ? colors.primary : colors.gray200}`,
                    background: activo ? colors.primarySubtle : colors.gray50,
                    color: activo ? colors.primary : colors.gray400,
                    fontSize: typography.sizes.sm, cursor: "pointer", textAlign: "center",
                    transition: "all 0.15s ease",
                    outline: esHoy ? `2px solid ${colors.primaryLight}` : "none",
                  }}
                >
                  {label} {d.getDate()}
                </button>
              );
            })}

            {Array.from(
              { length: Math.max(20, Number(dispo.endHour) || 18) - Math.min(8, Number(dispo.startHour) || 9) },
              (_, i) => Math.min(8, Number(dispo.startHour) || 9) + i
            ).map((h) => (
              <React.Fragment key={h}>
                <div style={{ fontSize: typography.sizes.sm, color: colors.gray400, textAlign: "right", paddingRight: 6, paddingTop: 4 }}>
                  {String(h).padStart(2, "0")}:00
                </div>
                {diasSemana.map((d) => {
                  const diaActivo = (dispo.availableDays || []).includes(d.getDay());
                  const enHorario = h >= (Number(dispo.startHour) || 0) && h < (Number(dispo.endHour) || 24);
                  const disponible = diaActivo && enHorario;
                  const llamadas = llamadasPorCelda[`${isoDia(d)}_${h}`] || [];
                  return (
                    <div
                      key={`${isoDia(d)}_${h}`}
                      style={{
                        minHeight: 30, borderRadius: 4, padding: 2,
                        background: disponible ? colors.primarySubtle : colors.gray50,
                        display: "flex", flexDirection: "column", gap: 2,
                      }}
                    >
                      {llamadas.map((c) => {
                        const st = (c.status || "pendiente");
                        const chipBg = st === "resuelta" ? colors.successLight : st === "respondida" ? colors.infoLight : colors.warningLight;
                        const chipTx = st === "resuelta" ? colors.successDark : st === "respondida" ? colors.infoDark : colors.warningDark;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => { setDispoOpen(false); abrirResponder(c); }}
                            title={`${c.call?.time} · ${c.residentName || "Residente"} — ${c.message || ""}`}
                            style={{
                              border: "none", borderRadius: 4, padding: "3px 5px", cursor: "pointer",
                              background: chipBg, color: chipTx, fontFamily: "inherit",
                              fontSize: 11, textAlign: "left", lineHeight: 1.25, fontWeight: 400,
                              overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                              transition: "all 0.15s ease",
                            }}
                          >
                            📞 {c.call?.time} {c.residentName || "Residente"}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

      </AdminModal>
    </div>
  );
}
